import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { Textarea } from "../components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { toast } from 'sonner'
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select"

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
})

function MapUpdater({ coordinates }) {
  const map = useMap()
  
  useEffect(() => {
    if (coordinates) {
      map.setView(coordinates, 16)
    }
  }, [coordinates, map])

  return null
}

export default function NewLead() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [coordinates, setCoordinates] = useState(null)
  const [salesReps, setSalesReps] = useState([])
  const [formData, setFormData] = useState({
    customer_name: '',
    contact_info: {
      email: '',
      phone: ''
    },
    address: {
      street: '',
      postal_code: '',
      city: '',
      country: 'Deutschland'
    },
    notes: '',
    status: 'new',
    sales_rep_id: user.id
  })

  const geocodeAddress = async (address) => {
    const query = `${address.street}, ${address.postal_code} ${address.city}, ${address.country}`
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`
      )
      const data = await response.json()
      
      if (data && data[0]) {
        return {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        }
      }
    } catch (error) {
      console.error('Geocoding-Fehler:', error)
    }
    return null
  }

  useEffect(() => {
    const updateCoordinates = async () => {
      const { address } = formData
      if (address.street && address.postal_code && address.city) {
        const coords = await geocodeAddress(address)
        if (coords) {
          setCoordinates([coords.lat, coords.lng])
        }
      }
    }
    updateCoordinates()
  }, [formData.address])

  useEffect(() => {
    if (user.role === 'admin') {
      fetchSalesReps()
    }
  }, [user.role])

  async function fetchSalesReps() {
    try {
      const users = await api.getUsers()
      setSalesReps(users.filter(u => u.status === 'active'))
    } catch (error) {
      console.error('Fehler beim Laden der Benutzer:', error)
      toast.error('Benutzer konnten nicht geladen werden')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      console.log('Sende Lead-Daten:', {
        ...formData,
        sales_rep_id: formData.sales_rep_id || user.id
      });

      const submitData = {
        ...formData,
        address: coordinates ? {
          ...formData.address,
          lat: coordinates[0],
          lng: coordinates[1]
        } : formData.address,
        sales_rep_id: formData.sales_rep_id || user.id
      }
      
      const response = await api.createLead(submitData)
      console.log('Lead erstellt:', response);
      toast.success('Lead erfolgreich erstellt')
      navigate('/leads')
    } catch (error) {
      console.error('Fehler beim Erstellen des Leads:', error)
      toast.error('Lead konnte nicht erstellt werden')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Neuer Lead</h1>
        <Button onClick={() => navigate('/leads')}>
          Zurück zur Übersicht
        </Button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Kundendaten</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Input
                  value={formData.customer_name}
                  onChange={(e) => handleInputChange('customer_name', e.target.value)}
                  placeholder="Kundenname"
                  required
                />
              </div>
              <div>
                <Input
                  type="email"
                  value={formData.contact_info.email}
                  onChange={(e) => handleInputChange('contact_info.email', e.target.value)}
                  placeholder="E-Mail"
                />
              </div>
              <div>
                <Input
                  value={formData.contact_info.phone}
                  onChange={(e) => handleInputChange('contact_info.phone', e.target.value)}
                  placeholder="Telefon"
                />
              </div>
              <div>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Notizen"
                />
              </div>
              {user.role === 'admin' && (
                <div>
                  <Select
                    value={formData.sales_rep_id || 'none'}
                    onValueChange={(value) => handleInputChange('sales_rep_id', value === 'none' ? null : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Benutzer auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Keiner</SelectItem>
                      {salesReps.map(rep => (
                        <SelectItem key={rep.id} value={rep.id}>
                          {rep.name || rep.email} ({rep.role === 'admin' ? 'Admin' : 'Vertriebler'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Adresse</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Input
                    value={formData.address.street}
                    onChange={(e) => handleInputChange('address.street', e.target.value)}
                    placeholder="Straße"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    value={formData.address.postal_code}
                    onChange={(e) => handleInputChange('address.postal_code', e.target.value)}
                    placeholder="PLZ"
                  />
                  <Input
                    value={formData.address.city}
                    onChange={(e) => handleInputChange('address.city', e.target.value)}
                    placeholder="Stadt"
                  />
                </div>
                <div>
                  <Input
                    value={formData.address.country}
                    onChange={(e) => handleInputChange('address.country', e.target.value)}
                    placeholder="Land"
                  />
                </div>
              </CardContent>
            </Card>

            {coordinates && (
              <Card>
                <CardHeader>
                  <CardTitle>Standort</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px] w-full rounded-lg overflow-hidden">
                    <MapContainer
                      center={coordinates}
                      zoom={16}
                      className="h-full w-full"
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <Marker position={coordinates} />
                      <MapUpdater coordinates={coordinates} />
                    </MapContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>

        <div className="mt-6">
          <Button type="submit" disabled={loading}>
            {loading ? 'Lädt...' : 'Lead erstellen'}
          </Button>
        </div>
      </form>
    </div>
  )
} 